import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Stack,
  Skeleton,
  IconButton,
  Collapse,
  Link as MuiLink
} from "@mui/material";
import { Colors } from "../utils/colors";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { DrawService } from "../services/draw/DrawService";
import { API } from "../constant/apiConstant";
import FlipCountdown from "../components/FlipCountdown";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

function getWinnerDisplayText(draw) {
  const minimumWinners = Number(draw?.minimum_winners || 10);
  const displayWinnerCount = Number(draw?.display_winner_count || minimumWinners);
  const expectedWinnerCount = Number(draw?.expected_winner_count || minimumWinners);
  const winnerPrizeAmount = Number(draw?.winner_prize_amount || 0);
  const payoutPool = Number(draw?.prize_pool || 0) * 0.5;
  const minimumThreshold = minimumWinners * winnerPrizeAmount;

  if (!winnerPrizeAmount || payoutPool < minimumThreshold || displayWinnerCount <= minimumWinners) {
    return `MINIMUM OF ${minimumWinners} WINNERS`;
  }

  return `EXPECTED ${expectedWinnerCount} WINNERS`;
}

const Home = () => {
  const navigate = useNavigate();
  const isAuthenticated = useSelector((state) => state.appState.isAuthenticated);
  const [nextDraws, setNextDraw] = useState(null);
  const [loadingDraw, setLoadingDraw] = useState(true);
  const [settings, setSettings] = useState(null);
  const [faqs, setFaqs] = useState([]);
  const [faqOpen, setFaqOpen] = useState(false);
  const [howToPlayGroups, setHowToPlayGroups] = useState([]);
  const [howToPlayOpen, setHowToPlayOpen] = useState(false);

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await fetch(`${API.BASE_URL}/api/settings`);
        const data = await response.json();

        if (data?.status) {
          setSettings(data.settings);
        }
      } catch (error) {
        console.log("Error fetching settings:", error);
      }
    };

    loadSettings();
  }, []);

  
  useEffect(() => {
    const loadFaqs = async () => {
      try {
        const response = await fetch(`${API.BASE_URL}/api/faqs`);
        const data = await response.json();

        if (data?.status) {
          setFaqs(data.faqs || []);
        }
      } catch (error) {
        console.log("Error fetching FAQs:", error);
      }
    };

    loadFaqs();
  }, []);

  useEffect(() => {
    const loadHowToPlayGroups = async () => {
      try {
        const response = await fetch(`${API.BASE_URL}/api/how-to-play`);
        const data = await response.json();

        if (data?.status) {
          setHowToPlayGroups(data.groups || []);
        }
      } catch (error) {
        console.log("Error fetching how-to-play groups:", error);
      }
    };

    loadHowToPlayGroups();
  }, []);

  useEffect(() => {
    let active=true,running=false;
    const fetchNextDraw = async () => {
      if(running)return;running=true;
      setLoadingDraw(true);
      try {
        const res = await DrawService.getNextDraw();
        if(active)setNextDraw(res?.id ? res : null);
      } catch (error) {
        if(active)setNextDraw(null);
        console.log("Error fetching draws:", error);
      } finally {running=false;if(active)setLoadingDraw(false);}
    };

    fetchNextDraw();
    const timer=setInterval(fetchNextDraw,15000);
    return ()=>{active=false;clearInterval(timer);};
  }, [isAuthenticated]);

  return (
    <Box
      sx={{
        backgroundColor: Colors.secondary,
        px: { xs: 2, md: 4 },
        pt: 0,
        pb: 0,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1100 }}>

        
        <Card
          sx={{
            p: { xs: 1.5, md: 4 },
            borderRadius: 1,
            background: "linear-gradient(180deg, #0b2a4a 0%, #071f36 100%)",
            color: Colors.text,
          }}
        >
          <Typography
            fontWeight={800}
            sx={{
              color: "#ffb300",
              fontSize: { xs: "1.42rem", sm: "1.65rem", md: "2.1rem" },
              lineHeight: 1.1,
              whiteSpace: "nowrap",
              letterSpacing: { xs: "-0.02em", sm: "normal" }
            }}
          >
            ONLINE RAFFLE DRAW
          </Typography>

          <Typography sx={{ mt: 0.5, color: Colors.whitesmoke }}>
            Fair Game, Fair Win
          </Typography>

          <Stack direction="column" spacing={0.5} sx={{ mt: 0.75 }}>
            <Chip
              label={settings?.minimum_age_notice || "18+ Only"}
              sx={{
                bgcolor: "#102f52",
                color: "#fbc02d",
                border: "1px solid #fbc02d",
                fontWeight: 600,
                borderRadius: 1,
                width: "fit-content"
              }}
            />
          </Stack>

          <Box
            sx={{
              mt: 0.5,
              p: 2,
              borderRadius: 1,
              border: "2px solid #ffb300",
              bgcolor: "rgba(255,179,0,0.05)",
              maxWidth: 420,
            }}
          >
            <Typography fontWeight={700} sx={{ color: "#ffb300" }}>
              NEXT DRAW
            </Typography>

            {!loadingDraw && nextDraws ? (
              <>
                <Typography
                  variant="h5"
                  fontWeight={800}
                  sx={{ mt: 1, color: "#ffffff" }}
                >
                  {nextDraws.draw_code}
                </Typography>

                <Typography sx={{ mt: 1, color: "#ffffff" }}>
                  Date:{" "}
                  {new Date(nextDraws.draw_date).toLocaleDateString("en-NG", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </Typography>

                <Typography sx={{ color: "#ffffff" }}>
                  Time:{" "}
                  {new Date(nextDraws.draw_date).toLocaleTimeString("en-NG", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                  })}
                </Typography>

                <Typography sx={{ mt: 1, color: "#ffffff" }}>
                  Prize Per Winner: ₦{Number(nextDraws.winner_prize_amount || 0).toLocaleString()}
                </Typography>

                <Typography sx={{ mt: 1, color: "#ffcc33", fontWeight: 700 }}>
                  {getWinnerDisplayText(nextDraws)}
                </Typography>

               <Box
  mt={3}
  sx={{
    display: "flex",
    justifyContent: "center",
    transform: {
      xs: "scale(0.75)",
      sm: "scale(0.85)",
      md: "scale(0.9)"
    },
    transformOrigin: "top center",
    width: "100%"
  }}
>
  <FlipCountdown targetDate={nextDraws.draw_date} />
</Box>

              </>
            ) : (
              loadingDraw ? (
                <Skeleton variant="rectangular" height={120} />
              ) : (
                <>
                  <Typography
                    variant="h5"
                    fontWeight={800}
                    sx={{ mt: 1, color: "#ffffff" }}
                  >
                    No Active Draw
                  </Typography>

                  <Typography sx={{ mt: 1, color: "#ffffff" }}>
                    A new draw session will appear here once it is published.
                  </Typography>

                  <Box
                    mt={3}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      transform: {
                        xs: "scale(0.75)",
                        sm: "scale(0.85)",
                        md: "scale(0.9)"
                      },
                      transformOrigin: "top center",
                      width: "100%"
                    }}
                  >
                    <FlipCountdown targetDate={null} />
                  </Box>
                </>
              )
            )}
          </Box>

          <Box
            sx={{
              mt: 0.5,
              p: 1.5,
              borderRadius: 1,
              border: "1px solid #fbc02d",
              bgcolor: "rgba(251,192,45,0.05)",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ cursor: "pointer" }}
              onClick={() => setHowToPlayOpen((prev) => !prev)}
            >
              <Typography fontWeight={700} sx={{ color: "#ffb300" }}>
                How To Play
              </Typography>
              <IconButton
                sx={{
                  color: "#fff",
                  transform: howToPlayOpen ? "rotate(0deg)" : "rotate(-90deg)",
                  transition: "transform 0.2s ease"
                }}
              >
                <ExpandMoreIcon />
              </IconButton>
            </Stack>

            <Collapse in={howToPlayOpen}>
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Stack spacing={1}>
                  {howToPlayGroups.length ? (
                    howToPlayGroups.map((group) => (
                      <Box
                        key={group.id}
                        sx={{
                          p: 1.25,
                          borderRadius: 1,
                          background: "rgba(255,255,255,0.04)",
                          border: "1px solid rgba(255,255,255,0.08)"
                        }}
                      >
                        <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                          {group.title}
                        </Typography>
                        {group.description ? (
                          <Typography sx={{ color: "rgba(255,255,255,0.78)", fontSize: 13, mt: 0.5 }}>
                            {group.description}
                          </Typography>
                        ) : null}

                        <Typography sx={{ color: "rgba(255,255,255,0.82)", fontSize: 13, mt: 1 }}>
                          View on:
                        </Typography>

                        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                          {[
                            { label: "Facebook", url: group.facebook_url },
                            { label: "Instagram", url: group.instagram_url },
                            { label: "X", url: "https://x.com/Fliplottery" }
                          ]
                            .filter((item) => Boolean(item.url))
                            .map((item) => (
                              <MuiLink
                                key={`${group.id}-${item.label}`}
                                href={item.url}
                                target="_blank"
                                rel="noreferrer"
                                underline="hover"
                                sx={{ color: "#39ff14", fontWeight: 700 }}
                              >
                                {item.label}
                              </MuiLink>
                            ))}
                        </Stack>
                      </Box>
                    ))
                  ) : null}
                </Stack>
              </Stack>
            </Collapse>
          </Box>

        </Card>

        
<Card
  sx={{
    mt: "5px",
    mb: 0,
    p: { xs: 2, md: 4 },
    borderRadius: 1,
    background: "linear-gradient(180deg,#0b2a4a,#071f36)",
  }}
>
  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
    <Typography variant="h4" fontWeight={700} sx={{ color: "#ffb300" }}>
      FAQ
    </Typography>

    <IconButton
      onClick={() => setFaqOpen((prev) => !prev)}
      sx={{
        color: "#fff",
        transform: faqOpen ? "rotate(0deg)" : "rotate(-90deg)",
        transition: "transform 0.2s ease"
      }}
    >
      <ExpandMoreIcon />
    </IconButton>
  </Stack>

  <Collapse in={faqOpen}>
  <Stack spacing={2}>

    {(faqs.length ? faqs : [
      {
        id: "fallback-1",
        question: "What is FlipLottery?",
        answer: "FlipLottery is a secure online lottery platform where users buy digital tickets and participate in transparent draws with verified payouts."
      },
      {
        id: "fallback-2",
        question: "How do I buy a ticket?",
        answer: "Navigate to the Buy Ticket page, choose the number of tickets you want and complete your payment securely."
      },
      {
        id: "fallback-3",
        question: "How are winners selected?",
        answer: `${settings?.rng_display_note || "Winners are selected using a certified Random Number Generator (RNG)"} to guarantee fairness and transparency.`
      },
      {
        id: "fallback-4",
        question: "When will I receive my winnings?",
        answer: "All winnings are transferred to your verified bank account within 48 hours after draw verification."
      }
    ]).map((faq) => (
    <Accordion
      key={faq.id}
      sx={{
        bgcolor: "#0f2d4d",
        borderRadius: 0.5,
        border: "0.5px solid #1e3a5f",
        "&:before": { display: "none" },
        color: "#ffffff"
      }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}>
        <Typography fontWeight={600}>
          {faq.question}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Typography>
          {faq.answer}
        </Typography>
      </AccordionDetails>
    </Accordion>
    ))}

  </Stack>
  </Collapse>
</Card>

      </Box>
    </Box>
  );
};
export default Home;
